import { useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Activity, User, CheckCircle, Clock, MessageSquare, FileText, AlertCircle } from 'lucide-react';
import type { Task, ActivityLogEntry } from '@/types';
import { formatTime, formatDateRelative } from '@/lib/utils';

interface ActivityFeedProps {
  tasks: Task[];
}

export function ActivityFeed({ tasks }: ActivityFeedProps) {
  const activities = useMemo(() => {
    const allActivities: (ActivityLogEntry & { taskTitle?: string })[] = [];
    
    tasks.forEach((task) => {
      task.activityLog.forEach((log) => {
        allActivities.push({
          ...log,
          taskTitle: task.title
        });
      });
    });
    
    return allActivities.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    ).slice(0, 10);
  }, [tasks]);

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'Task Received':
        return { icon: FileText, color: 'text-blue-600 bg-blue-50' };
      case 'Developer Assigned':
        return { icon: User, color: 'text-purple-600 bg-purple-50' };
      case 'Build Started':
        return { icon: Activity, color: 'text-green-600 bg-green-50' };
      case 'Ready for Review':
        return { icon: Clock, color: 'text-amber-600 bg-amber-50' };
      case 'Delivered':
        return { icon: CheckCircle, color: 'text-green-600 bg-green-50' };
      case 'Approved':
        return { icon: CheckCircle, color: 'text-blue-600 bg-blue-50' };
      case 'Revision Requested':
        return { icon: AlertCircle, color: 'text-red-600 bg-red-50' };
      case 'Comment Added':
        return { icon: MessageSquare, color: 'text-slate-600 bg-slate-50' };
      default:
        return { icon: Activity, color: 'text-slate-600 bg-slate-50' };
    }
  };

  return (
    <Card title="Activity Feed" subtitle="Real-time updates on your projects">
      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
        {activities.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-5 top-3 bottom-3 w-px bg-slate-200"></div>
            
            {activities.map((activity) => {
              const config = getActivityIcon(activity.action);
              const Icon = config.icon;
              const date = new Date(activity.timestamp);
              
              return (
                <div key={activity.id} className="flex gap-4 relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900">{activity.action}</span>
                      <span className="text-xs text-slate-400">
                        {formatDateRelative(date)} at {formatTime(date)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{activity.description}</p>
                    {activity.taskTitle && (
                      <p className="text-xs text-slate-400 mt-1">
                        on <span className="font-medium text-slate-500">{activity.taskTitle}</span>
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">by {activity.actor}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
